/* eslint-disable no-unused-vars */
/* eslint-disable no-console */
import { useContext, useEffect, useState, useRef } from 'react';
import { Helmet } from 'react-helmet';
import toast from 'react-hot-toast';
import { LazyLoadImage } from 'react-lazy-load-image-component';
import 'react-lazy-load-image-component/src/effects/blur.css';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';

import useChatStore from 'Components/Chat/store';

import { inject, observer } from 'mobx-react';
import { TranslateContext } from '../../Context/translate-context';
import config from '../../config';
// Forms
import { yupResolver } from '@hookform/resolvers/yup';
import { useForm } from 'react-hook-form';
import * as yup from 'yup';

import useTreeDNDChatStore from 'Components/Chat/TreeDNDChat/Store';
import ReCAPTCHA from 'react-google-recaptcha';

import StraicoIcon from '../../Theme/icons/straico';

import RobotOpen from '../../Theme/icons/robotBase';
import RobotWink from '../../Theme/icons/robotWink';
import RobotBlink from '../../Theme/icons/robotBlink';
import RobotClosed from '../../Theme/icons/robotClosed';

import {
  labelStyle,
  labelStyleFirst,
  inputField,
  mainButton,
  secondaryButton,
  secondaryButtonText,
  formTitle,
} from './FormStyles';

const baseURL = config.baseURL;

const LoginLayout = ({ children, isPasswordFocused }) => {
  const [isWinking, setIsWinking] = useState(false);
  const [isBlinking, setIsBlinking] = useState(false);

  const handleWink = () => {
    if (isWinking) return;

    setIsWinking(true);
    setTimeout(() => {
      setIsWinking(false);
    }, 2000);
  };

  useEffect(() => {
    const blinkInterval = setInterval(() => {
      if (!isWinking && !isPasswordFocused) {
        setIsBlinking(true);
        setTimeout(() => {
          setIsBlinking(false);
        }, 200);
      }
    }, 4000 + Math.random() * 3000);

    return () => clearInterval(blinkInterval);
  }, [isWinking, isPasswordFocused]);

  const RobotImage = isPasswordFocused
    ? RobotClosed
    : isWinking
    ? RobotWink
    : isBlinking
    ? RobotBlink
    : RobotOpen;

  return (
    <>
      <Helmet>
        <title>Start - Straico</title>
        <meta
          name="description"
          content="Log in or sign up to Straico — your all-in-one AI workspace for text, image, video and audio generation using 50+ AI models."
        />
      </Helmet>

      <div className="h-screen flex flex-row items-center justify-center bg-seasalt md:bg-[#0B0B0E] overflow-scroll md:overflow-hidden pt-[60px]">
        <div className="flex items-center justify-center w-full lg:w-2/5 xl:w-1/3 md:p-8">
          <div className="bg-seasalt md:rounded-3xl md:shadow-2xl p-8 w-full md:relative md:mt-4 md:ml-0">
            <div
              className="absolute -top-6 -right-6 z-10 cursor-pointer hidden md:inline"
              onClick={handleWink}
            >
              <div className="bg-nue-blue rounded-2xl shadow-lg w-[110px] h-[110px] flex items-center justify-center">
                <RobotImage className="w-full h-full object-contain" />
              </div>
            </div>
            <div className="flex flex-col items-center w-full md:hidden space-y-4 -mb-8">
              <StraicoIcon className="fill-nue-blue h-[30px] w-auto" />
              <div
                className="bg-nue-blue rounded-2xl shadow-lg w-[110px] h-[110px] flex items-center justify-center overflow-hidden"
                onClick={handleWink}
              >
                <RobotImage className="w-full h-full object-contain" />
              </div>
            </div>
            {children}
          </div>
        </div>

        <div className="hidden lg:flex lg:w-3/5 xl:w-2/3 relative h-screen overflow-hidden">
          <div className="flex-1 flex items-center justify-center p-8">
            <div className="w-full max-w-4xl">
              <LazyLoadImage
                className="w-full h-auto object-contain"
                src="/images/straico-product.png"
                effect="blur"
                alt="Straico Features"
              />
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export const LoginSection = inject('store')(
  observer(({ store }) => {
    const [error, setError] = useState(null);
    const { translator } = useContext(TranslateContext);
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const navigate = useNavigate();
    const [isPasswordFocused, setIsPasswordFocused] = useState(false);

    const [captchaValue, setCaptchaValue] = useState('');

    const onChange = (val, attr) => {
      if (attr === 'email') setEmail(val);
      if (attr === 'password') setPassword(val);
      setError('');
    };

    const handleCaptchaChange = (value) => {
      setCaptchaValue(value);
    };

    const onLogin = async (e) => {
      try {
        if (!captchaValue && import.meta.env.VITE_CAPTCHA_REQUIRED === 'TRUE') {
          toast.error(
            "Please verify that you're human by completing the captcha"
          );
          return;
        }
        e.preventDefault();
        const response = await fetch(baseURL + 'auth/signin', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            email: email,
            password: password,
          }),
        });
        const data = await response.json();
        // Handle error
        if ([401, 400, 404].includes(response.status)) {
          if (data?.message) {
            toast.error(data?.message);
            return;
          }
        }
        /* This commented code is for disable the need of email verification before show the /chat page
        store.loginWithDataTokenAndProfile(data);
        store.sendMetricsEvent('login', null, { method: 'user-pass' });
        if (sessionStorage.getItem('redirectUrl')) {
          navigate(sessionStorage.getItem('redirectUrl'));
        } else {
          navigate('/chat');
        }*/

        // Navigate to chat page if user is verified
        if (data?.profile?.verified) {
          store.loginWithDataTokenAndProfile(data);
          store.sendMetricsEvent('login', null, { method: 'user-pass' });
          if (sessionStorage.getItem('redirectUrl')) {
            navigate(sessionStorage.getItem('redirectUrl'));
          } else {
            navigate('/chat');
          }
        } else {
          toast(
            'Your account needs to be activated. Kindly check your email inbox and confirm.',
            {
              icon: '⚠️',
              duration: 10000,
            }
          );
        }
      } catch (err) {
        if (err?.response?.data?.message) {
          toast.error(err?.response?.data?.message);
        }
      }
    };

    const signUpGoogle = async (event) => {
      if (store.fpr) {
        window.open(baseURL + 'auth/google/callback?fpr=' + store.fpr, '_self');
      } else {
        window.open(baseURL + 'auth/google/callback', '_self');
      }
    };

    const { cleanTreeData } = useTreeDNDChatStore();
    const { cleanChatList, cleanSelectedChat } = useChatStore();

    const handleSubmit = (e) => {
      e.preventDefault();
      cleanTreeData();
      cleanChatList();
      cleanSelectedChat();
      onLogin(e);
    };

    return (
      <LoginLayout isPasswordFocused={isPasswordFocused}>
        <form onSubmit={handleSubmit}>
          <div className="bg-seasalt w-full max-w-md mx-auto pt-16 lg:pt-4 font-figtree">
            <div className={formTitle}>
              {translator('sectionLogIn.labelTitle')}
            </div>
            <div className="mb-4">
              <label className={labelStyleFirst}>
                {translator('sectionLogIn.fieldEmail')}
              </label>
              <input
                value={email}
                onChange={(e) => onChange(e.target.value, 'email')}
                type="email"
                className={inputField}
                placeholder={translator('sectionLogIn.placeholderEmail')}
              />
            </div>
            <div className="mb-4">
              <label className={labelStyle}>
                {translator('sectionLogIn.fieldPassword')}
              </label>
              <input
                value={password}
                onChange={(e) => onChange(e.target.value, 'password')}
                type="password"
                className={inputField}
                placeholder={translator('sectionLogIn.placeholderPassword')}
                onFocus={() => setIsPasswordFocused(true)}
                onBlur={() => setIsPasswordFocused(false)}
              />
            </div>
            <div className="text-right mb-4">
              <NavLink to="/forgot">
                <p className="text-violet-blue text-sm">
                  Forgot your password?
                </p>
              </NavLink>
            </div>
            {import.meta.env.VITE_CAPTCHA_REQUIRED === 'TRUE' && (
              <div className="flex justify-center mt-4 mb-2">
                <ReCAPTCHA
                  sitekey={import.meta.env.VITE_GOOGLE_CAPTCHA_KEY}
                  onChange={handleCaptchaChange}
                  style={{
                    transform: 'scale(0.77)',
                    WebkitTransform: 'scale(0.77)',
                    transformOrigin: 'center center',
                    WebkitTransformOrigin: 'center center',
                  }}
                />
              </div>
            )}

            {error ? <div className="text-red-600">{error}</div> : null}
            <div className="flex flex-col mb-2 mt-4 items-center">
              <button type="submit" className={mainButton}>
                {translator('sectionLogIn.buttonLogIn')}
              </button>
            </div>
            <div className="flex flex-col text-center mb-4 w-full items-center">
              <p className="text-sm">or</p>
              <div onClick={signUpGoogle} className={secondaryButton}>
                <span>
                  <img
                    src="/images/logo-google-oauth.png"
                    className="mr-2"
                  ></img>
                </span>
                <span className="uppercase text-sm text-violet-blue">
                  Sign in with Google
                </span>
              </div>
            </div>
            <div className="text-center mt-0">
              <p className="text-sm">
                New on Straico?{' '}
                <NavLink to="/signup" className="text-violet-blue text-sm">
                  Sign up
                </NavLink>
              </p>
            </div>
          </div>
        </form>
      </LoginLayout>
    );
  })
);

const schemaValidationSignUp = yup.object({
  email: yup.string().email().required(),
  password: yup.string().required(),
  fname: yup
    .string()
    .required('First name is required')
    .matches(
      /^[A-Za-z0-9\s]+$/,
      'First name should only contain letters and numbers'
    ),
  lname: yup
    .string()
    .required('Last name is required')
    .matches(
      /^[A-Za-z0-9\s]+$/,
      'Last name should only contain letters and numbers'
    ),
});

export const SignupSection = inject('store')(
  observer(({ store }) => {
    const { translator } = useContext(TranslateContext);
    const [error, setError] = useState(null);

    const [captchaValue, setCaptchaValue] = useState('');

    const [fprValue, setFprValue] = useState(null);

    const [loadingSingUp, setLoadingSingUp] = useState(false);

    const [isPasswordFocused, setIsPasswordFocused] = useState(false);

    const location = useLocation();
    const navigate = useNavigate();

    useEffect(() => {
      const currentURL = location.search;
      const urlParams = new URLSearchParams(currentURL);
      // const fprValue = urlParams.get('fpr');
      setFprValue(urlParams.get('fpr'));
    }, [location.search]);

    const {
      register,
      handleSubmit,
      control,
      formState: { errors },
    } = useForm({
      defaultValues: {
        email: '',
        password: '',
        fname: '',
        lname: '',
      },
      resolver: yupResolver(schemaValidationSignUp),
    });

    const handleCaptchaChange = (value) => {
      setCaptchaValue(value);
    };

    const onSignup = async ({ email, password, fname, lname }) => {
      setLoadingSingUp(true);
      try {
        setError('');
        if (!captchaValue && import.meta.env.VITE_CAPTCHA_REQUIRED === 'TRUE') {
          toast.error(
            "Please verify that you're human by completing the captcha"
          );
          setLoadingSingUp(false);
          return;
        }
        await fetch(baseURL + 'auth/signup', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            email,
            password,
            fname,
            lname,
            referral: store.referral,
            fpr: fprValue,
          }),
        }).then(async (response) => {
          const data = await response.json();
          toast(data.message, {
            icon: '✉️',
            duration: 12000,
          });
          // Handle error
          if ([401, 404].includes(response.status)) {
            if (data?.message) {
              toast.error(data?.message);
              setLoadingSingUp(false);
              return;
            }
          }
          // Navigate to chat page if user is verified
          if (data?.profile?.verified) {
            store.loginWithDataTokenAndProfile(data);
            store.sendMetricsEvent('login', null, { method: 'signup' });
            if (sessionStorage.getItem('redirectUrl')) {
              navigate(sessionStorage.getItem('redirectUrl'));
              setLoadingSingUp(false);
            } else {
              navigate('/chat');
              setLoadingSingUp(false);
            }
          }
        });
      } catch (err) {
        if (err?.response?.data?.message) {
          toast.error(err?.response?.data?.message);
        }
      }
    };

    const signUpGoogle = async (event) => {
      if (fprValue) {
        window.open(baseURL + 'auth/google/callback?fpr=' + fprValue, '_self');
      } else {
        window.open(baseURL + 'auth/google/callback', '_self');
      }
    };

    const onSubmit = (data) => {
      store?.setFpr(fprValue);
      onSignup(data);
    };

    const ErrorMessage = ({ message }) => (
      <div className="pl-2 mt-2">
        <p className="text-red-600 text-xs italic">{message}</p>
      </div>
    );

    return (
      <LoginLayout isPasswordFocused={isPasswordFocused}>
        <form
          className="pt-8 pb-6 lg:pt-0 lg:pb-0 font-figtree"
          onSubmit={handleSubmit(onSubmit)}
        >
          <div className="bg-seasalt w-full max-w-md mx-auto pt-8 lg:pt-4">
            <div className={formTitle}>
              {translator('sectionSignUp.labelTitle')}
            </div>
            <div className="flex flex-row gap-4">
              <div className="flex-1">
                <label className={labelStyleFirst}>
                  {translator('sectionSignUp.fieldFirstName')}
                </label>
                <input
                  {...register('fname')}
                  type="text"
                  className={inputField}
                  placeholder={translator('sectionSignUp.placeholderFirstName')}
                />
                {errors.fname && (
                  <ErrorMessage message={errors.fname.message} />
                )}
              </div>
              <div className="flex-1">
                <label className={labelStyleFirst}>
                  {translator('sectionSignUp.fieldLastName')}
                </label>
                <input
                  {...register('lname')}
                  type="text"
                  className={inputField}
                  placeholder={translator('sectionSignUp.placeholderLastName')}
                />
                {errors.lname && (
                  <ErrorMessage message={errors.lname.message} />
                )}
              </div>
            </div>
            <div>
              <label className={labelStyle}>
                {translator('sectionSignUp.fieldEmail')}
              </label>
              <input
                {...register('email')}
                type="email"
                className={inputField}
                placeholder={translator('sectionSignUp.placeholderEmail')}
              />
              {errors.email && <ErrorMessage message={errors.email.message} />}
            </div>
            <div className="mb-4">
              <label className={labelStyle}>
                {translator('sectionSignUp.fieldPassword')}
              </label>
              <input
                {...register('password')}
                type="password"
                className={inputField}
                placeholder={translator('sectionSignUp.placeholderPassword')}
                onFocus={() => setIsPasswordFocused(true)}
                onBlur={() => setIsPasswordFocused(false)}
              />
              {errors.password && (
                <ErrorMessage message={errors.password.message} />
              )}
            </div>
            {error ? <div className="text-red-600">{error}</div> : null}
            {import.meta.env.VITE_CAPTCHA_REQUIRED === 'TRUE' && (
              <div className="flex justify-center mt-4 mb-2">
                <ReCAPTCHA
                  sitekey={import.meta.env.VITE_GOOGLE_CAPTCHA_KEY}
                  onChange={handleCaptchaChange}
                  style={{
                    transform: 'scale(0.77)',
                    WebkitTransform: 'scale(0.77)',
                    transformOrigin: 'center center',
                    WebkitTransformOrigin: 'center center',
                  }}
                />
              </div>
            )}
            <div className="flex flex-col mb-2 mt-4 items-center">
              <button type="submit" className={mainButton}>
                {translator('sectionSignUp.buttonSignUp')}
              </button>
              {loadingSingUp && (
                <div className="text-center my-2">
                  <p className="text-sm">
                    Didn't receive an email? Please contact us at{' '}
                    <NavLink
                      to="mailto:hello@straico.com"
                      className="text-violet-blue text-sm"
                    >
                      hello@straico.com
                    </NavLink>{' '}
                    and we'll activate your account
                  </p>
                </div>
              )}
            </div>
            <div className="flex flex-col text-center mb-4 w-full items-center">
              <p className="text-sm">or</p>
              <div onClick={signUpGoogle} className={secondaryButton}>
                <span>
                  <img
                    src="/images/logo-google-oauth.png"
                    className="mr-2"
                  ></img>
                </span>
                <span className="uppercase text-sm text-violet-blue">
                  Sign in with Google
                </span>
              </div>
            </div>
            <div className="text-center mb-2">
              <p className="text-sm">
                Already in Straico?{' '}
                <NavLink to="/login" className="text-violet-blue text-sm">
                  Log in
                </NavLink>
              </p>
            </div>
          </div>
        </form>
      </LoginLayout>
    );
  })
);

export const ForgotSection = () => {
  const { translator } = useContext(TranslateContext);
  const [error, setError] = useState(null);
  const [email, setEmail] = useState('');

  const onChange = (val, attr) => {
    if (attr === 'email') setEmail(val);
    setError('');
  };

  const onForgot = async (e) => {
    try {
      e.preventDefault();
      setError('');
      await fetch(baseURL + '/forgot/mail', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: email,
        }),
      }).then(async (response) => {
        const data = await response.json();
        toast(data.message, {
          icon: '⚠️',
          duration: 9000,
        });
      });
    } catch (err) {
      if (err?.response?.data?.message) {
        toast.error(err?.response?.data?.message);
      }
    }
  };
  return (
    <LoginLayout>
      <form onSubmit={onForgot}>
        <div className="bg-seasalt w-full max-w-md mx-auto pt-16 lg:pt-4 font-figtree">
          <div className="mt-3 mb-6">
            <div className={formTitle}>Reset password</div>
          </div>
          <div className="mt-3 mb-6 text-center">
            <p className="text-sm text-cool-gray">
              Enter your email address below and submit. We will send you a mail
              containing a password reset link.
            </p>
          </div>
          <div className="mb-6">
            <label className={labelStyle}>
              {translator('sectionSignUp.fieldEmail')}
            </label>
            <input
              value={email}
              onChange={(e) => onChange(e.target.value, 'email')}
              type="email"
              className={inputField}
              placeholder="kate@mail.com"
            />
          </div>
          <div className="flex flex-col mb-4 items-center">
            <button type="submit" className={mainButton}>
              Send email
            </button>
          </div>
          <div className="text-center">
            <NavLink to="/login" className="text-violet-blue text-sm">
              Back to login
            </NavLink>
          </div>
          <div className="text-center mt-8">
            <p className="text-sm">
              <a
                href="https://www.straico.com/"
                target="_blank"
                className="text-violet-blue text-sm"
                rel="noreferrer"
              >
                Learn more about us
              </a>
            </p>
          </div>
        </div>
      </form>
    </LoginLayout>
  );
};

const LanguageSelectorComponent = ({ changer }) => {
  return (
    <div className="container mx-auto text-right mt-2">
      <button
        className="hover:bg-green-500 bg-gray-200 font-medium rounded-md text-xs px-3 py-1 hover:text-white mt-2 inline-block"
        onClick={() => changer('en')}
      >
        EN
      </button>
      <button
        className="hover:bg-green-500 bg-gray-200 font-medium rounded-md text-xs px-3 py-1 hover:text-white mt-2 inline-block"
        onClick={() => changer('es')}
      >
        ES
      </button>
    </div>
  );
};
